import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { getCarpetasFiltros } from '../../services/Carpetas';
import DetalleCarpeta from './DetalleCarpeta';
import { useStateContext } from '../../contexts/ContextProvider';
import DetailsIconButton from '../../components/IconButtons/DetailsIconButton';

// ─── Estilos compartidos con el resto de páginas ─────────────────────────────

const customStyles = {
    table: {
        style: { backgroundColor: 'white', borderRadius: '8px' },
    },
    head: {
        style: { fontSize: '14px', fontWeight: 'bold', color: '#2F336A', backgroundColor: '#F2F5FE', borderBottom: '1px solid #D9E1F2' },
    },
    headRow: {
        style: { minHeight: '48px' },
    },
    headCells: {
        style: { paddingLeft: '12px', paddingRight: '12px', whiteSpace: 'nowrap' },
    },
    rows: {
        style: { fontSize: '13px', backgroundColor: 'white', minHeight: '52px', borderBottom: '1px solid #F3F4F6', transition: 'background-color 0.2s ease-in-out' },
        highlightOnHoverStyle: { backgroundColor: '#F9FAFB', borderBottomColor: '#E5E7EB', outline: 'none' },
    },
    cells: {
        style: { paddingLeft: '12px', paddingRight: '12px' },
    },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function estadoBadgeClass(estado) {
    switch (estado) {
        case 'Completado':    return 'bg-green-100 text-green-700';
        case 'Pendiente':     return 'bg-yellow-100 text-yellow-700';
        case 'En Revisión':   return 'bg-blue-100 text-blue-700';
        case 'Salida de Obra':return 'bg-gray-100 text-gray-700';
        default:              return 'bg-gray-100 text-gray-700';
    }
}

function ProgresoDoc({ carpeta }) {
    const docs = carpeta.documentosRequeridos || [];
    const total = docs.length;
    const subidos = docs.filter((d) => d.idArchivo).length;
    const hoy = new Date();
    const vencidos = docs.filter(
        (d) => d.idArchivo && d.fechaVencimiento && new Date(d.fechaVencimiento) < hoy
    ).length;

    if (total === 0) return <span className="text-xs text-gray-400">Sin docs</span>;

    return (
        <span className={`text-xs font-semibold font-mono ${vencidos > 0 ? 'text-red-600' : subidos === total ? 'text-green-600' : 'text-gray-500'}`}>
            {subidos}/{total}
            {vencidos > 0 && <span className="font-normal text-red-500 ml-1">{vencidos} venc.</span>}
        </span>
    );
}

// ─── Componente principal ─────────────────────────────────────────────────────

// Una fila por equipo: la carpeta vigente si tiene, o si no la última que
// tuvo (Salida de Obra/Traspaso) — así el equipo sigue siendo accesible
// (con su Historial y Anexos completos dentro del detalle) aunque ya no
// tenga ningún proceso activo, sin llenar la tabla con una fila por cada
// carpeta cerrada.
function unaFilaPorEquipo(carpetas) {
    const porEquipo = new Map();
    const sinCodigo = [];

    for (const c of carpetas) {
        if (!c.codigoEquipo) { sinCodigo.push(c); continue; }
        const actual = porEquipo.get(c.codigoEquipo);
        if (!actual) { porEquipo.set(c.codigoEquipo, c); continue; }

        const actualVigente = actual.estado !== 'Salida de Obra';
        const cVigente = c.estado !== 'Salida de Obra';
        if (cVigente && !actualVigente) {
            porEquipo.set(c.codigoEquipo, c);
        } else if (cVigente === actualVigente && new Date(c.fechaCreacion) > new Date(actual.fechaCreacion)) {
            porEquipo.set(c.codigoEquipo, c);
        }
    }

    return [...porEquipo.values(), ...sinCodigo];
}

const FichaEquipo = () => {
    const { handleClick, isClicked } = useStateContext();
    const [carpetas, setCarpetas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busqueda, setBusqueda] = useState('');
    const [seleccionado, setSeleccionado] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            // 'TODOS' (a diferencia de 'ALL') incluye también carpetas en Salida de
            // Obra/Traspaso, para poder elegir la más reciente de cada equipo aunque
            // ya no tenga ninguna vigente.
            const data = await getCarpetasFiltros('', '', '', '', 'ALL', 'TODOS', '');
            setCarpetas(unaFilaPorEquipo(data || []));
        } catch {
            setCarpetas([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const handleVer = (row) => {
        setSeleccionado(row);
        handleClick('detalleFichaEquipo');
    };

    const handleRefresh = (opcion) => {
        if (opcion === 'REFRESH') fetchData();
    };

    const filtradas = carpetas.filter((c) => {
        if (!busqueda) return true;
        const b = busqueda.toLowerCase();
        return (
            (c.codigoEquipo || '').toLowerCase().includes(b) ||
            (c.patente || '').toLowerCase().includes(b) ||
            (c.serie || '').toLowerCase().includes(b) ||
            (c.idMaquina?.nombreMaquina || '').toLowerCase().includes(b)
        );
    });

    const columns = [
        {
            name: '',
            width: '40px',
            cell: (row) => (
                <DetailsIconButton onClick={() => handleVer(row)} title="Ver carpeta" />
            ),
            ignoreRowClick: true,
        },
        {
            name: 'Código',
            selector: (r) => r.codigoEquipo,
            sortable: true,
            cell: (r) => <span className="font-mono font-semibold text-gray-800">{r.codigoEquipo || '—'}</span>,
            width: '120px',
        },
        {
            name: 'Tipo máquina',
            selector: (r) => r.idMaquina?.nombreMaquina,
            sortable: true,
            cell: (r) => <span className="text-gray-600">{r.idMaquina?.nombreMaquina || '—'}</span>,
            width: '200px',
        },
        {
            name: 'Patente',
            selector: (r) => r.patente,
            sortable: true,
            cell: (r) => <span>{r.patente || '—'}</span>,
            width: '100px',
        },
        {
            name: 'Serie',
            selector: (r) => r.serie,
            sortable: true,
            cell: (r) => <span>{r.serie || '—'}</span>,
            width: '110px',
        },
        {
            name: 'Año',
            selector: (r) => r.anno,
            sortable: true,
            cell: (r) => <span>{r.anno || '—'}</span>,
            width: '70px',
        },
        {
            name: 'Estado',
            selector: (r) => r.estado,
            sortable: true,
            cell: (r) => (
                <span className={`text-xs px-2 py-1 rounded-full text-center ${estadoBadgeClass(r.estado)}`}>
                    {r.estado}
                </span>
            ),
            width: '130px',
        },
        {
            name: 'Documentos',
            cell: (r) => <ProgresoDoc carpeta={r} />,
            width: '110px',
        },
    ];

    return (
        <>
            <div className="mx-4 lg:mx-12 mt-4">
                {/* Título */}
                <div className="flex justify-between items-center px-4 py-2 border-b border-gray-200 bg-white rounded-t-md shadow-sm">
                    <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />
                        <p className="text-gray-800 font-semibold text-base md:text-lg">Ficha del Equipo</p>
                    </div>
                </div>

                {/* Filtros */}
                <div className="bg-white p-6 rounded-lg shadow-lg mt-2">
                    <div className="flex justify-start items-center p-2 md:ml-6 md:mr-6 relative top-1">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 w-full">
                            <div className="flex flex-col col-span-2">
                                <label className="text-xs font-semibold mb-1 whitespace-nowrap">Buscar</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Código, patente, serie, tipo de máquina..."
                                        value={busqueda}
                                        onChange={(e) => setBusqueda(e.target.value)}
                                        className="text-xs border border-gray-300 p-2 rounded w-full pr-7"
                                    />
                                    {busqueda && (
                                        <button
                                            onClick={() => setBusqueda('')}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm leading-none"
                                        >×</button>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-col justify-end">
                                <span className="text-xs text-gray-400 pb-2">
                                    {filtradas.length} {filtradas.length === 1 ? 'equipo' : 'equipos'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabla */}
                <div className="bg-white p-6 rounded-lg shadow-lg items-end mt-4">
                    <div className="grid grid-cols-1 gap-4">
                        <div className="flex flex-col">
                            <DataTable
                                columns={columns}
                                data={filtradas}
                                progressPending={loading}
                                progressComponent={<span className="text-sm text-gray-400 py-8">Cargando equipos...</span>}
                                noDataComponent={<span className="text-sm text-gray-400 py-8">No hay equipos que coincidan con la búsqueda.</span>}
                                highlightOnHover
                                pagination
                                striped
                                className="border border-gray-300 rounded-lg shadow-sm"
                                noHeader
                                customStyles={customStyles}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Drawer de detalle */}
            {isClicked.detalleFichaEquipo && seleccionado && (
                <DetalleCarpeta
                    data={seleccionado}
                    onChangeDatos={handleRefresh}
                />
            )}
        </>
    );
};

export default FichaEquipo;
